import { useState } from "react";
import AppLayout from "@/components/layout/AppLayout";
import { StatusBadge } from "@/components/shared/StatusBadge";
import { useDemoState } from "@/data/demoState";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";
import {
  Swords, Clock, AlertTriangle, Paperclip, User, Calendar,
  Copy, ExternalLink, Send, FileText, MessageSquare, ArrowRight, Sparkles, Mail, Info, Users
} from "lucide-react";
import { Separator } from "@/components/ui/separator";

export default function RfiWarRoom() {
  const { rfiItems } = useDemoState();
  const [selectedRfi, setSelectedRfi] = useState(rfiItems[0] || null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [draftMode, setDraftMode] = useState(null);

  const allItems = rfiItems.flatMap((r) => r.items.map((item) => ({ ...item, rfiRef: r.rfiRef, project: r.project, council: r.council })));

  const draftEmails = {
    consultant: `Dear {{consultant}},\n\nRe: {{project}} - {{rfiRef}}\n\nWe are responding to a Request for Further Information from {{council}} Council and require your input on the following item:\n\nItem: {{title}}\nDescription: {{description}}\n\nPlease provide your response or upload the required documentation using the secure link below:\n\n[Secure Upload Link]\n\nDue date: {{dueDate}}\n\nPlease let us know if you need any clarification.\n\nKind regards,\nSarah Chen\nBuilder PM`,
    council: `Dear Planning Department,\n\nRe: {{rfiRef}} - {{project}}\n\nPlease find enclosed our response to the Request for Further Information dated {{receivedDate}}.\n\nThe following items have been addressed:\n\n{{items}}\n\nPlease do not hesitate to contact us if further information is required.\n\nKind regards,\nSarah Chen\nBuilder PM`,
  };

  return (
    <AppLayout>
      <div className="space-y-4" data-testid="rfi-war-room-page">
        {/* Page explainers */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Card className="border-blue-100 bg-blue-50/30">
            <CardContent className="py-3 px-4 flex items-start gap-2.5">
              <Info className="w-4 h-4 text-blue-600 mt-0.5 shrink-0" />
              <div>
                <p className="text-xs font-semibold text-blue-700 mb-0.5">What this page does</p>
                <p className="text-xs text-blue-600">Council requests broken down into owned tasks. Track due dates, draft responses, and see what is blocking each item.</p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-slate-200 bg-slate-50/30">
            <CardContent className="py-3 px-4 flex items-start gap-2.5">
              <ArrowRight className="w-4 h-4 text-slate-500 mt-0.5 shrink-0" />
              <div>
                <p className="text-xs font-semibold text-slate-600 mb-0.5">What happens next</p>
                <p className="text-xs text-slate-500">Chase consultants for responses. Draft council replies. Clearing items reduces blockers and moves stage readiness forward.</p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-slate-200 bg-slate-50/30">
            <CardContent className="py-3 px-4 flex items-start gap-2.5">
              <Users className="w-4 h-4 text-slate-500 mt-0.5 shrink-0" />
              <div>
                <p className="text-xs font-semibold text-slate-600 mb-0.5">Who uses this</p>
                <p className="text-xs text-slate-500">Builder PM, permit admin, office admin. Anyone managing council request responses.</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Council request overview cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {rfiItems.map((rfi) => {
            const blocker = rfi.items.filter((i) => i.isBlocker).length;
            const done = rfi.items.filter((i) => i.status === "evidence-submitted" || i.status === "cleared").length;
            return (
              <Card
                key={rfi.id}
                className={`border-slate-200 cursor-pointer transition-all hover:shadow-md ${selectedRfi?.id === rfi.id ? "ring-2 ring-blue-200 border-blue-300" : ""}`}
                onClick={() => setSelectedRfi(rfi)}
                data-testid={`rfi-card-${rfi.id}`}
              >
                <CardContent className="py-4 px-5">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <Badge variant="outline" className="text-[10px] font-mono">{rfi.rfiRef}</Badge>
                        <StatusBadge status={rfi.status} size="sm" />
                      </div>
                      <p className="text-sm font-medium text-slate-900">{rfi.project}</p>
                      <p className="text-xs text-slate-400">{rfi.council} Council</p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-slate-400">Due {rfi.dueDate}</p>
                      <p className={`text-sm font-semibold ${rfi.daysRemaining <= 7 ? "text-red-600" : "text-slate-600"}`}>
                        {rfi.daysRemaining} days left
                      </p>
                    </div>
                  </div>
                  <Progress value={(done / rfi.items.length) * 100} className="h-1.5 mt-3" />
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-[11px] text-slate-400">{done}/{rfi.items.length} items addressed</span>
                    {blocker > 0 && (
                      <span className="text-[11px] text-red-600 font-medium flex items-center gap-1">
                        <AlertTriangle className="w-3 h-3" /> {blocker} blockers
                      </span>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Selected RFI detail */}
        {selectedRfi && (
          <Card className="border-slate-200" data-testid="rfi-detail">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-base font-heading">{selectedRfi.rfiRef} - {selectedRfi.project}</CardTitle>
                  <p className="text-xs text-slate-400 mt-1">Received {selectedRfi.receivedDate} from {selectedRfi.council} Council</p>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" className="gap-1.5 text-xs" data-testid="rfi-generate-pack-btn">
                    <FileText className="w-3.5 h-3.5" /> Generate response pack
                  </Button>
                  <Button size="sm" variant="outline" className="gap-1.5 text-xs" onClick={() => setDraftMode("council")} data-testid="rfi-draft-council-btn">
                    <Mail className="w-3.5 h-3.5" /> Draft council response
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {/* RFI Items Table */}
              <div className="border border-slate-200 rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-slate-50/50 border-b border-slate-100">
                      <th className="text-left py-2.5 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider w-10">#</th>
                      <th className="text-left py-2.5 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Item</th>
                      <th className="text-left py-2.5 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Owner</th>
                      <th className="text-left py-2.5 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Due</th>
                      <th className="text-left py-2.5 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Blocker</th>
                      <th className="text-left py-2.5 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Status</th>
                      <th className="text-right py-2.5 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedRfi.items.map((item) => (
                      <tr
                        key={item.id}
                        className={`border-b border-slate-100 last:border-0 hover:bg-slate-50/50 transition-colors cursor-pointer ${item.isBlocker ? "bg-red-50/20" : ""}`}
                        onClick={() => setSelectedItem(item)}
                        data-testid={`rfi-item-row-${item.id}`}
                      >
                        <td className="py-3 px-4 font-mono text-xs text-slate-400">{item.number}</td>
                        <td className="py-3 px-4">
                          <p className="font-medium text-slate-900">{item.title}</p>
                          <p className="text-xs text-slate-400 mt-0.5 truncate max-w-xs">{item.description}</p>
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2">
                            <div className="w-6 h-6 bg-slate-200 rounded-full flex items-center justify-center text-[10px] font-medium text-slate-600">
                              {item.owner.split(" ").map(n => n[0]).join("")}
                            </div>
                            <div>
                              <p className="text-xs text-slate-700">{item.owner}</p>
                              <p className="text-[10px] text-slate-400">{item.ownerRole}</p>
                            </div>
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <p className="text-xs text-slate-600">{item.dueDate}</p>
                          <p className={`text-[10px] font-medium ${item.daysRemaining <= 7 ? "text-red-600" : "text-slate-400"}`}>
                            {item.daysRemaining} days
                          </p>
                        </td>
                        <td className="py-3 px-4">
                          {item.isBlocker ? (
                            <span className="inline-flex items-center gap-1 text-red-600 text-xs font-medium">
                              <AlertTriangle className="w-3 h-3" /> Yes
                            </span>
                          ) : (
                            <span className="text-xs text-slate-400">No</span>
                          )}
                        </td>
                        <td className="py-3 px-4"><StatusBadge status={item.status} size="sm" /></td>
                        <td className="py-3 px-4 text-right">
                          <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={(e) => { e.stopPropagation(); setDraftMode("consultant"); setSelectedItem(item); }}>
                            <Send className="w-3 h-3 mr-1" /> Request
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Item Detail Sheet */}
        <Sheet open={!!selectedItem && !draftMode} onOpenChange={() => setSelectedItem(null)}>
          <SheetContent className="w-full sm:max-w-lg overflow-y-auto" data-testid="rfi-item-detail-sheet">
            {selectedItem && (
              <>
                <SheetHeader>
                  <SheetTitle className="font-heading text-lg">Item {selectedItem.number}: {selectedItem.title}</SheetTitle>
                  <SheetDescription>{selectedItem.description}</SheetDescription>
                </SheetHeader>
                <div className="mt-5 space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-slate-50 rounded-lg p-3">
                      <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-1">Owner</p>
                      <p className="text-sm font-medium text-slate-900">{selectedItem.owner}</p>
                      <p className="text-xs text-slate-400">{selectedItem.ownerRole}</p>
                    </div>
                    <div className="bg-slate-50 rounded-lg p-3">
                      <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-1">Due Date</p>
                      <p className="text-sm font-medium text-slate-900">{selectedItem.dueDate}</p>
                      <p className={`text-xs ${selectedItem.daysRemaining <= 7 ? "text-red-600" : "text-slate-400"}`}>
                        {selectedItem.daysRemaining} days remaining
                      </p>
                    </div>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-3">
                    <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-1">Status</p>
                    <StatusBadge status={selectedItem.status} />
                    {selectedItem.isBlocker && <StatusBadge status="blocker" className="ml-2" />}
                  </div>
                  {selectedItem.attachments.length > 0 && (
                    <div>
                      <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-2">Attachments</p>
                      {selectedItem.attachments.map((att, i) => (
                        <div key={i} className="flex items-center gap-2 bg-slate-50 rounded-lg p-2.5">
                          <Paperclip className="w-3.5 h-3.5 text-slate-400" />
                          <span className="text-sm text-slate-700">{att}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  {selectedItem.notes && (
                    <div className="bg-amber-50/50 border border-amber-100 rounded-lg p-3">
                      <p className="text-xs text-amber-700">{selectedItem.notes}</p>
                    </div>
                  )}
                  <Separator />
                  <div className="space-y-2">
                    <p className="text-xs font-semibold text-slate-500">Actions</p>
                    <div className="flex flex-wrap gap-2">
                      <Button size="sm" variant="outline" className="gap-1.5 text-xs" onClick={() => { setDraftMode("consultant"); }} data-testid="draft-consultant-request">
                        <Send className="w-3 h-3" /> Draft consultant request
                      </Button>
                      <Button size="sm" variant="outline" className="gap-1.5 text-xs" data-testid="draft-builder-update">
                        <MessageSquare className="w-3 h-3" /> Draft builder update
                      </Button>
                    </div>
                  </div>
                </div>
              </>
            )}
          </SheetContent>
        </Sheet>

        {/* Draft Email Sheet */}
        <Sheet open={!!draftMode} onOpenChange={() => setDraftMode(null)}>
          <SheetContent className="w-full sm:max-w-lg overflow-y-auto" data-testid="draft-email-sheet">
            <SheetHeader>
              <SheetTitle className="font-heading text-lg flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-blue-600" />
                AI Draft: {draftMode === "consultant" ? "Consultant Request" : "Council Response"}
              </SheetTitle>
              <SheetDescription>
                Review and edit before copying. This email is never auto-sent.
              </SheetDescription>
            </SheetHeader>
            <div className="mt-5 space-y-4">
              <div className="bg-white border border-slate-200 rounded-lg p-4">
                <pre className="text-sm text-slate-700 whitespace-pre-wrap font-body leading-relaxed">
                  {draftMode === "consultant"
                    ? draftEmails.consultant
                        .replace("{{consultant}}", selectedItem?.owner || "Consultant")
                        .replace("{{project}}", selectedRfi?.project || "")
                        .replace("{{rfiRef}}", selectedRfi?.rfiRef || "")
                        .replace("{{council}}", selectedRfi?.council || "")
                        .replace("{{title}}", selectedItem?.title || "")
                        .replace("{{description}}", selectedItem?.description || "")
                        .replace("{{dueDate}}", selectedItem?.dueDate || "")
                    : draftEmails.council
                        .replace("{{rfiRef}}", selectedRfi?.rfiRef || "")
                        .replace("{{project}}", selectedRfi?.project || "")
                        .replace("{{receivedDate}}", selectedRfi?.receivedDate || "")
                        .replace("{{items}}", selectedRfi?.items.map((i) => `- Item ${i.number}: ${i.title}`).join("\n") || "")
                  }
                </pre>
              </div>
              <div className="flex gap-2">
                <Button size="sm" className="bg-slate-900 text-white hover:bg-slate-800 gap-1.5" data-testid="copy-draft-btn">
                  <Copy className="w-3.5 h-3.5" /> Copy to clipboard
                </Button>
                <Button size="sm" variant="outline" className="gap-1.5" data-testid="open-email-client-btn">
                  <ExternalLink className="w-3.5 h-3.5" /> Open in email client
                </Button>
              </div>
              <div className="bg-blue-50/50 border border-blue-100 rounded-lg p-3">
                <p className="text-xs text-blue-700">
                  This draft is AI-generated for convenience. Review, edit as needed, and send from your own email client. easypermit never sends emails automatically.
                </p>
              </div>
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </AppLayout>
  );
}
